import { FC, useMemo } from 'react';
import { Box, Text, VStack, Code } from '@chakra-ui/react';
import {
  AIPrompt,
  PromptWeights,
  InbreedingThresholds,
  DEFAULT_PROMPT_WEIGHTS,
  DEFAULT_INBREEDING_THRESHOLDS,
} from '@/types/aiManagement';

interface PromptPreviewProps {
  weights?: PromptWeights;
  thresholds?: InbreedingThresholds;
  customInstructions?: string;
}

/**
 * 生成されるプロンプトのプレビューコンポーネント
 * AIに送信される指示文を非エンジニアにも分かりやすく表示
 */
export const PromptPreview: FC<PromptPreviewProps> = ({
  weights = DEFAULT_PROMPT_WEIGHTS,
  thresholds = DEFAULT_INBREEDING_THRESHOLDS,
  customInstructions,
}) => {
  const previewText = useMemo(() => {
    const lines = [
      'あなたは犬のブリーディング専門家です。繁殖学と遺伝学の深い知識を持ち、健康で優れた子犬を生み出すための交配提案を行います。',
      '',
      '## 評価基準',
      '',
      `1. **遺伝的多様性（${weights.geneticDiversity}%）**: 近親係数が低いほど高評価。遺伝的な健康を重視します。`,
      `2. **繁殖実績（${weights.breedingHistory}%）**: 過去の出産データに基づく成功率を評価します。`,
      `3. **特徴マッチング（${weights.traitMatch}%）**: 性格、体格、毛色などの相性を評価します。`,
      '',
      '## 近親係数の判定基準',
      '',
      `- **安全ゾーン**: ${thresholds.safe}%未満 - 推奨される交配`,
      `- **注意ゾーン**: ${thresholds.safe}%〜${thresholds.caution}% - 慎重な検討が必要`,
      `- **警告ゾーン**: ${thresholds.caution}%〜${thresholds.warning}% - リスクが高い`,
      `- **危険ゾーン**: ${thresholds.warning}%以上 - 交配を推奨しない`,
    ];

    if (customInstructions) {
      lines.push('', '## 追加指示', '', customInstructions);
    }

    return lines.join('\n');
  }, [weights, thresholds, customInstructions]);

  return (
    <VStack align="stretch" gap={3}>
      <Box>
        <Text fontSize="sm" color="gray.600" mb={2}>
          このような指示がAIに送られます：
        </Text>
        <Box
          bg="gray.50"
          p={4}
          borderRadius="md"
          borderWidth={1}
          borderColor="gray.200"
          maxH="400px"
          overflowY="auto"
        >
          <Code
            display="block"
            whiteSpace="pre-wrap"
            fontSize="xs"
            bg="transparent"
            p={0}
          >
            {previewText}
          </Code>
        </Box>
      </Box>

      {/* 説明 */}
      <Box bg="blue.50" p={3} borderRadius="md">
        <Text fontSize="xs" color="blue.700">
          <Text as="span" fontWeight="bold">
            プレビューについて：
          </Text>
          これはAIに送信される指示の概要です。実際の分析時には、選択したメス犬と候補オス犬の
          詳細情報も一緒に送信されます。
        </Text>
      </Box>
    </VStack>
  );
};

interface PromptPreviewFromDataProps {
  prompt: AIPrompt;
}

/**
 * AIPromptオブジェクトからプレビューを生成
 */
export const PromptPreviewFromData: FC<PromptPreviewFromDataProps> = ({ prompt }) => {
  return (
    <PromptPreview
      weights={prompt.weights}
      thresholds={prompt.inbreedingThresholds}
      customInstructions={prompt.customInstructions}
    />
  );
};
